import type { HysaRate } from "../data/hysa-rates.js";

/**
 * HYSA comparison helpers — Phase 8 Session 3.
 *
 * Lives in web/src/lib/ per the 5.1 lock (web does NOT import from
 * @hearth/shared). Pure-frontend math; no backend dependency.
 */

/**
 * Annualized opportunity cost in cents of staying at the current APY
 * vs a comparator. Only positive deltas (better-than-current) are
 * relevant; ties + worse rates return 0.
 */
export function annualOpportunityCostCents(args: {
  readonly user_balance_cents: number;
  readonly current_apy_bp: number;
  readonly comparator_apy_bp: number;
}): number {
  const delta = args.comparator_apy_bp - args.current_apy_bp;
  if (delta <= 0) return 0;
  // bp × balance_cents / 10000 = annual interest delta in cents.
  return Math.floor((delta * args.user_balance_cents) / 10000);
}

/**
 * Rank `comparators` by APY descending, dropping any whose
 * `min_balance_cents` exceeds `user_balance_cents`. Returns at most
 * `limit` rows with annualized opportunity cost vs the user's current
 * APY.
 */
export function compareHysa(args: {
  readonly user_balance_cents: number;
  readonly current_apy_bp: number;
  readonly comparators: readonly HysaRate[];
  readonly limit?: number;
}): Array<{
  readonly comparator: HysaRate;
  readonly annual_opportunity_cents: number;
  readonly delta_bp: number;
}> {
  const eligible = args.comparators.filter(
    (c) => c.min_balance_cents <= args.user_balance_cents,
  );
  const ranked = eligible
    .map((c) => ({
      comparator: c,
      annual_opportunity_cents: annualOpportunityCostCents({
        user_balance_cents: args.user_balance_cents,
        current_apy_bp: args.current_apy_bp,
        comparator_apy_bp: c.apy_bp,
      }),
      delta_bp: c.apy_bp - args.current_apy_bp,
    }))
    .sort((a, b) => b.comparator.apy_bp - a.comparator.apy_bp);
  return ranked.slice(0, args.limit ?? 20);
}

/** True if the snapshot is older than ~6 months (HYSA churn). */
export function isHysaSnapshotStale(
  asOf: string,
  todayIso: string = new Date().toISOString().slice(0, 10),
): boolean {
  const a = Date.parse(asOf);
  const t = Date.parse(todayIso);
  if (Number.isNaN(a) || Number.isNaN(t)) return false;
  return (t - a) / 86_400_000 > 180;
}
